import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LogOut, LayoutGrid, Building2, Sparkles, Users, Inbox, ClipboardCheck, ListTodo, DollarSign, LinkIcon, Package, FlaskConical, BookOpen, BookOpenCheck, HeartHandshake, Workflow, FileText, ScanSearch, Settings } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { NeptuneLogo } from "../../components/neptune/NeptuneMark";
import { LEGAL_PAGES } from "../../lib/legal";
import DocsCommandPalette from "../../components/admin/DocsCommandPalette";
import TotpGate from "../../components/admin/TotpGate";
import BrainHealthPill from "../../components/admin/BrainHealthPill";

const SITE_PAGES = [
  { key: "homepage", label: "Homepage" },
  { key: "solutions", label: "Solutions" },
  { key: "industries", label: "Industries" },
  { key: "about", label: "About" },
  { key: "contact", label: "Contact" },
  { key: "footer", label: "Footer" },
  { key: "nav", label: "Navigation" },
  { key: "meta", label: "Meta / SEO" },
];

const LEGAL_ADMIN_PAGES = LEGAL_PAGES.map((p) => ({
  key: `legal.${p.slug}`,
  label: p.title,
}));

// Union list consumed by PageEditor to resolve the current page meta.
const PAGES = [...SITE_PAGES, ...LEGAL_ADMIN_PAGES];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center font-body">
        <div className="text-zinc-500 text-[11px] tracking-[0.22em] uppercase">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace state={{ from: pathname }} />;
  // Feb 2026 — Solutions Module 1: four roles can access the back office
  // (super_admin, admin, compliance, sales). Module 5 RBAC tightens
  // specific routes; this gate only checks "is a team member."
  const TENANT_ROLES = ["super_admin", "admin", "compliance", "sales"];
  if (!TENANT_ROLES.includes(user.role)) return <Navigate to="/admin/login" replace />;
  const isSuperAdmin = user.role === "super_admin";
  // Module 2 — Solutions catalog is visible to all management roles + sales.
  // Module 5 (RBAC) will hide buy rates and contracts for sales role inside
  // the page itself. Compliance does NOT see Solutions per the matrix.
  const showSolutions = ["super_admin", "admin", "sales"].includes(user.role);

  const NavItem = ({ to, label, testId }) => (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        `rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
          isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
        }`
      }
    >
      {label}
    </NavLink>
  );

  return (
    <TotpGate>
    <div className="min-h-screen bg-[#0a0a0a] text-white font-body flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-white/[0.06] p-6 overflow-y-auto">
        <a href="/" className="inline-flex items-center mb-10">
          <NeptuneLogo height={22} />
        </a>
        <BrainHealthPill />
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-600 mb-3">— Workspace</div>

        <NavLink
          to="/admin/dashboard"
          end
          data-testid="admin-nav-dashboard"
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
              isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          <LayoutGrid className="h-3.5 w-3.5" /> Dashboard
        </NavLink>

        <NavLink
          to="/admin/pipeline"
          data-testid="admin-nav-pipeline"
          className={({ isActive }) =>
            `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
              isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          <Users className="h-3.5 w-3.5" /> Pipeline
        </NavLink>

        <NavLink
          to="/admin/merchants"
          data-testid="admin-nav-merchants"
          className={({ isActive }) =>
            `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
              isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          <Building2 className="h-3.5 w-3.5" /> Merchants
        </NavLink>

        <NavLink
          to="/admin/onboarding-links"
          data-testid="admin-nav-onboarding-links"
          className={({ isActive }) =>
            `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
              isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          <LinkIcon className="h-3.5 w-3.5" /> Magic links
        </NavLink>

        {showSolutions && (
          <NavLink
            to="/admin/solutions"
            data-testid="admin-nav-solutions"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
                isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            <Package className="h-3.5 w-3.5" /> Solutions
          </NavLink>
        )}

        {/* Brief 3.5 PR 2 §1.4 — single Nerea nav entry instead of 6.
            Internal tabs (Autonomy / Roster / Eval / Usage / Rehearsal /
            Reassurance) live inside the AdminNereaShell sub-shell. */}
        <NavLink
          to="/admin/nerea"
          data-testid="admin-nav-nerea"
          className={({ isActive }) =>
            `mt-7 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
              isActive || pathname.startsWith("/admin/nerea") || pathname === "/admin/ops/autonomy-rehearsal" || pathname === "/admin/ops/reassurance"
                ? "bg-white/[0.05] text-white"
                : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          <Sparkles className="h-3.5 w-3.5" /> Nerea
        </NavLink>

        <div className="mt-7 text-[10.5px] uppercase tracking-[0.22em] text-zinc-600 mb-3">— Ops</div>
        <NavLink
          to="/admin/ops/inbox"
          data-testid="admin-nav-ops-inbox"
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
              isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          <Inbox className="h-3.5 w-3.5" /> Inbox
        </NavLink>
        <NavLink
          to="/admin/ops/pending"
          data-testid="admin-nav-ops-pending"
          className={({ isActive }) =>
            `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
              isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          <ListTodo className="h-3.5 w-3.5" /> Pending
        </NavLink>

        {isSuperAdmin && (
          <NavLink
            to="/admin/compliance/re-review"
            data-testid="admin-nav-compliance-re-review"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
                isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            <ScanSearch className="h-3.5 w-3.5" /> Re-review
          </NavLink>
        )}

        {isSuperAdmin && (
          <NavLink
            to="/admin/onboarding-sessions"
            data-testid="admin-nav-onboarding-sessions"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
                isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            <Workflow className="h-3.5 w-3.5" /> Onboarding sessions
          </NavLink>
        )}

        {isSuperAdmin && (
          <NavLink
            to="/admin/ops/paytech-faq"
            data-testid="admin-nav-ops-paytech-faq"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
                isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            <BookOpen className="h-3.5 w-3.5" /> PayTech FAQ
          </NavLink>
        )}

        {isSuperAdmin && (
          <NavLink
            to="/admin/ops/neptune-platform-knowledge"
            data-testid="admin-nav-ops-neptune-platform-knowledge"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
                isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            <BookOpenCheck className="h-3.5 w-3.5" /> Platform knowledge
          </NavLink>
        )}

        <NavLink
          to="/admin/docs/manual"
          data-testid="admin-nav-docs-manual"
          className={({ isActive }) =>
            `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
              isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
            }`
          }
        >
          <FileText className="h-3.5 w-3.5" /> Ops manuals
        </NavLink>

        {isSuperAdmin && (
          <NavLink
            to="/admin/team"
            data-testid="admin-nav-team"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
                isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            <Users className="h-3.5 w-3.5" /> Team
          </NavLink>
        )}

        {isSuperAdmin && (
          <NavLink
            to="/admin/system-settings"
            data-testid="admin-nav-system-settings"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
                isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            <Settings className="h-3.5 w-3.5" /> System settings
          </NavLink>
        )}

        <div className="mt-7 text-[10.5px] uppercase tracking-[0.22em] text-zinc-600 mb-3">— Pages</div>
        <nav className="flex flex-col gap-0.5">
          {SITE_PAGES.map((p) => (
            <NavItem
              key={p.key}
              to={`/admin/pages/${p.key}`}
              label={p.label}
              testId={`admin-nav-${p.key}`}
            />
          ))}
        </nav>

        <div className="mt-7 text-[10.5px] uppercase tracking-[0.22em] text-zinc-600 mb-3">— Legal</div>
        <nav className="flex flex-col gap-0.5">
          <NavLink
            to="/admin/legal"
            data-testid="admin-nav-legal"
            className={({ isActive }) =>
              `rounded-xl px-3 py-2 text-[13.5px] transition-colors ${
                isActive ? "bg-white/[0.05] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`
            }
          >
            All policies <span className="text-zinc-600">· {LEGAL_ADMIN_PAGES.length}</span>
          </NavLink>
        </nav>

        <div className="mt-auto pt-8">
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#0055FF] flex items-center justify-center text-[11px] font-medium text-white">
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] text-white truncate">{user.name || "Admin"}</div>
              <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 mt-0.5" data-testid="admin-nav-role-pill">
                {(user.role || "admin").replace("_", " ")}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              title="Logout"
              className="h-7 w-7 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.05] transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 p-6 sm:p-10">
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between mb-8">
          <a href="/admin/dashboard"><NeptuneLogo height={20} /></a>
          <button onClick={logout} className="text-[12px] text-zinc-400 hover:text-white">Logout</button>
        </div>
        <Outlet />
      </main>
      <DocsCommandPalette />
    </div>
    </TotpGate>
  );
}

export { PAGES, SITE_PAGES, LEGAL_ADMIN_PAGES };
